import React from "react";
import {
  getAppointments,
  getDictationList,
  getDocuments,
  getIVRCalls,
} from "apiHandler/actions";
import { getCareOpportunityList } from "apiHandler/actions/clinical";
import AudioIcon from "components/AudioIcon";
import { dateFormatter } from "components/helpers";
import dayjs from "dayjs";
import { Box, Button, Checkbox, IconButton } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { getFormattedDate, trimmedDate } from "utils/helper";

export const appointmentColumns = () => [
  {
    field: "patient_id",
    headerName: "ID",
    align: "left",
    width: 200,
  },
  {
    field: "id",
    headerName: "ID",
    width: 100,
    align: "center",
  },
  {
    field: "patient_name",
    headerName: "Patient Name",
    minWidth: 200,
    flex: 1,
    renderCell: (e) => (
      <div>
        {e.row.first_name} {e.row.last_name}
      </div>
    ),
  },
  {
    field: "dob",
    headerName: "DOB",
    width: 150,
    renderCell: (e) => <div>{trimmedDate(e.value)}</div>,
  },
  {
    field: "dos",
    headerName: "Date of service",
    width: 150,
    align: "center",
    renderCell: (e) => <div>{e.value && trimmedDate(e.value)}</div>,
  },
  {
    field: "startDate",
    headerName: "Time",
    width: 100,
    renderCell: (e) => <div>{e.row.dos_time}</div>,
  },
  {
    field: "appt_type",
    headerName: "Appnt Type",
    minWidth: 120,
    maxWidth: 200,
    flex: 1,
  },
  {
    field: "confirm_status",
    headerName: "Status",
    width: 170,
  },
  {
    field: "reminder_status",
    headerName: "Reminder Status",
    width: 150,
  },
];

export const dictationColumns = (
  currentPlaying,
  setCurrentPlaying,
  practice_id
) => [
  {
    field: "dos",
    headerName: "Date of service",
    minWidth: 120,
    flex: 1,
    valueFormatter: (value) => {
      if (!value) {
        return;
      }
      return getFormattedDate(value);
    },
    valueGetter: dateFormatter,
  },
  {
    field: "first_name",
    headerName: "Patient Name",
    minWidth: 60,
    flex: 1,
    renderCell: (e) => {
      return <div className="ml-10">{e.row.first_name}</div>;
    },
  },
  {
    field: "title",
    headerName: "Title",
    minWidth: 100,
    flex: 1,
    renderCell: (e) => {
      return <div className="ml-10">{e.row.title}</div>;
    },
  },
  {
    field: "audio_duration",
    headerName: "Recording",
    minWidth: 140,
    flex: 1,
    filterable: false,
    renderCell: (e) => {
      return (
        e.value && (
          <div className="list-duration ml-10">
            {e.value}
            <Box ml={2}>
              <AudioIcon
                showPlayer={true}
                id={e.row.id}
                currentPlaying={currentPlaying}
                setCurrentPlaying={setCurrentPlaying}
                practice_id={practice_id}
                npi={e.row.npi}
                ext={e.row.file_extension}
              />
            </Box>
          </div>
        )
      );
    },
  },
  {
    field: "provider_first_name",
    headerName: "Doctor’s / Practice Name",
    minWidth: 150,
    flex: 1,
    renderCell: (e) => {
      return <div className="ml-10">{e.row.provider_name}</div>;
    },
  },
  {
    field: "updated_by",
    headerName: "Last updated By",
    minWidth: 200,
    flex: 1,
    renderCell: (e) => <div className="ml-10">{e.value}</div>,
  },
  {
    field: "status",
    headerName: "Status",
    minWidth: 200,
    flex: 1,
    renderCell: (e) => <div className="ml-10">{e.value}</div>,
  },
  {
    field: "updated_date",
    headerName: "Last updated",
    minWidth: 125,
    flex: 1,
    valueFormatter: (value) => {
      if (!value) {
        return;
      }
      return getFormattedDate(value);
    },
    valueGetter: dateFormatter,
  },
  {
    field: "id",
    headerName: "ID",
    minWidth: 90,
    flex: 1,
    align: "center",
    renderCell: (e) => <div className="ml-10">{e.value}</div>,
  },
];

export const careColumns = () => [
  {
    field: "patient_id",
    headerName: "ID",
    minWidth: 100,
    flex: 1,
  },
  {
    field: "care_oppt_category",
    headerName: "Care Opportunity Category",
    minWidth: 180,
    flex: 1,
  },
  {
    field: "ext_source_name",
    headerName: "External Source Name",
    minWidth: 180,
    flex: 1,
  },
  { field: "status", headerName: "Status", minWidth: 150, flex: 1 },
  {
    field: "outreach_status",
    headerName: "Outreach Status",
    minWidth: 150,
    flex: 1,
  },
  {
    field: "created_date",
    headerName: "Created Date",
    minWidth: 150,
    flex: 1,
    valueGetter: (e) => dateFormatter(e),
    valueFormatter: (value) => (value ? getFormattedDate(value) : ""),
  },
  {
    field: "assigned_user_name",
    headerName: "Assigned User",
    minWidth: 180,
    flex: 1,
  },
  { field: "patient_id", headerName: "ID", minWidth: 100, flex: 1 },
  { field: "patient_name", headerName: "Name", minWidth: 100, flex: 1 },
  {
    field: "patient_dob",
    headerName: "DOB",
    minWidth: 100,
    flex: 1,
    valueGetter: (e) => dateFormatter(e),
    valueFormatter: (value) => (value ? getFormattedDate(value) : ""),
  },
  { field: "patient_age", headerName: "Age", minWidth: 100, flex: 1 },
  { field: "patient_gender", headerName: "Sex", minWidth: 100, flex: 1 },
  { field: "patient_phone", headerName: "Phone", minWidth: 150, flex: 1 },
  { field: "provider_name", headerName: "Provider", minWidth: 180, flex: 1 },
  {
    field: "last_obs_bp",
    headerName: "Last Observed BP",
    minWidth: 150,
    flex: 1,
  },
  {
    field: "last_obs_bp_date",
    headerName: "Last Observed BP Date",
    minWidth: 180,
    flex: 1,
    valueGetter: (e) => dateFormatter(e),
    valueFormatter: (value) => (value ? getFormattedDate(value) : ""),
  },
  {
    field: "bp_category",
    headerName: "HTN Category/Diagnosis",
    minWidth: 180,
    flex: 1,
  },
  {
    field: "action_rcmd",
    headerName: "Recommended Action",
    minWidth: 180,
    flex: 1,
  },
  {
    field: "next_appt_type",
    headerName: "Next Appt Type",
    minWidth: 180,
    flex: 1,
  },
  {
    field: "next_appt_date",
    headerName: "Next Appt Date",
    minWidth: 180,
    flex: 1,
    valueGetter: (e) => dateFormatter(e),
    valueFormatter: (value) => (value ? getFormattedDate(value) : ""),
  },
  {
    field: "next_appt_time",
    headerName: "Next Appt Time",
    minWidth: 150,
    flex: 1,
  },
];

export const documentColumns = (
  onDeleteDoc,
  includeClaimState,
  handleIncludeClaimChange,
  getDocumentPdf,
  downloading
) => [
  {
    field: "id",
    headerName: "Id",
    minWidth: 100,
  },
  {
    field: "document_type",
    headerName: "Type",
    minWidth: 100,
  },
  {
    field: "filename",
    headerName: "File Name",
    minWidth: 180,
    renderCell: (e) => {
      return (
        <Button
          variant="text"
          disabled={downloading}
          onClick={() => getDocumentPdf(e.row)}
        >
          {e.value}
        </Button>
      );
    },
  },
  {
    field: "filetitle",
    headerName: "Title",
    minWidth: 100,
  },
  { field: "filedescription", headerName: "Description", minWidth: 150 },
  {
    field: "document_scope",
    headerName: "Document Scope",
    minWidth: 100,
  },
  {
    field: "last_updated_dt",
    headerName: "Uploaded Date",
    minWidth: 150,

    valueGetter: (e) => dateFormatter(e),
    valueFormatter: (value) => (value ? getFormattedDate(value) : ""),
  },
  {
    field: "last_updated_by",
    headerName: "Uploaded by",
    minWidth: 120,
  },
  {
    field: "charge_entry_id",
    headerName: "Charge Entry Id",
    minWidth: 100,
    flex: 1,
  },
  {
    field: "include_claim",
    headerName: "Include in Claim",
    minWidth: 100,
    flex: 1,
    renderCell: (e) => {
      const isChecked =
        includeClaimState[e.row.id] ?? e.row.include_claim === "true";

      return (
        <Checkbox
          checked={isChecked}
          onChange={(event) =>
            handleIncludeClaimChange(e.row, event.target.checked)
          }
        />
      );
    },
  },
  {
    field: "",
    headerName: "Action",
    minWidth: 100,
    flex: 1,
    renderCell: (e) => (
      <IconButton onClick={() => onDeleteDoc(e.row)}>
        <DeleteIcon color="error" />
      </IconButton>
    ),
  },
];

export const callColumns = (currentPlaying, setCurrentPlaying) => [
  {
    field: "practice_ivr_call_details_id",
    headerName: "ID",
    width: 70,
    align: "center",
  },
  {
    field: "caller_type",
    headerName: "Caller Type",
    width: 100,
    align: "center",
  },
  {
    field: "agent_name",
    headerName: "Agent",
    width: 100,
    align: "center",
  },
  {
    field: "call_duration",
    headerName: "Recording",
    width: 140,
    filterable: false,
    renderCell: (e) => {
      return (
        e.value &&
        e.row.is_audio_exists === "Yes" && (
          <div className="list-duration ml-10">
            {e.value}
            <Box ml={2}>
              <AudioIcon
                id={e.row.practice_ivr_call_details_id}
                showPlayer={true}
                currentPlaying={currentPlaying}
                setCurrentPlaying={setCurrentPlaying}
                practice_id={e.row.practice_id}
                npi={e.row.npi}
                ext={e.row.file_extension}
                isIVRCall={true}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
              />
            </Box>
          </div>
        )
      );
    },
  },
  {
    field: "category",
    headerName: "Category",
    width: 150,
    align: "center",
  },
  {
    field: "direction",
    headerName: "Direction",
    width: 140,
  },
  {
    field: "date_started",
    headerName: "Received Date",
    width: 180,
    renderCell: (e) => (
      <div className="ml-10">
        {e.value ? dayjs(e.value).tz().format("MMM DD, hh:mm A") : "N/A"}
      </div>
    ),
  },
  {
    field: "origination_number",
    headerName: "Caller Number",
    width: 150,
    align: "center",
  },
  {
    field: "caller_name",
    headerName: "Caller Name",
    width: 150,
  },
  {
    field: "practice_location",
    headerName: "Location",
    width: 80,
    align: "center",
  },
  {
    field: "transcribed_text",
    headerName: "Text",
    flex: 1,
    minWidth: 150,
  },
];

export const tabs = (practice_id, patient_id) => [
  {
    label: "Appointments",
    fetchFunction: getAppointments,
    payload: {
      search_criteria: {
        practice_id,
        patient_id,
      },
    },
  },
  {
    label: "Dictations",
    fetchFunction: getDictationList,
    payload: {
      type: "Dictation",
      search_criteria: {
        practice_id,
        patient_id,
      },
    },
  },
  {
    label: "Documents",
    fetchFunction: getDocuments,
    payload: {
      practice_id,
      patient_id,
    },
  },
  {
    label: "Call Records",
    fetchFunction: getIVRCalls,
    payload: {
      practice_id,
      patient_id,
    },
  },
  // { label: "Claims", fetchFunction: null },
  // { label: "Bills", fetchFunction: null },
  {
    label: "Care Opportunities",
    fetchFunction: getCareOpportunityList,
    payload: {
      type: "CareOpportunity",
      practice_id,
      patient_id,
    },
  },
];
